import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Star, ArrowUpRight, MessageSquareQuote } from "lucide-react";
import { Reveal, Eyebrow } from "./primitives";
import { API } from "../lib/site";

export const StarRow = ({ rating, size = 16 }) => (
    <span className="inline-flex gap-1" aria-label={`Rated ${rating} out of 5`} role="img">
        {[1, 2, 3, 4, 5].map((s) => (
            <Star
                key={s}
                size={size}
                aria-hidden="true"
                className={s <= rating ? "fill-[#C2A160] text-[#C2A160]" : "text-[#C2A160]/30"}
            />
        ))}
    </span>
);

export const ReviewsPreview = () => {
    const [reviews, setReviews] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/reviews`)
            .then((res) => setReviews(res.data.reviews || []))
            .catch(() => setReviews([]));
    }, []);

    return (
        <section data-testid="reviews-section" className="bg-[#F2EFE7] py-24 sm:py-32">
            <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
                <div className="flex flex-wrap items-end justify-between gap-8">
                    <Reveal>
                        <Eyebrow>Community Reviews</Eyebrow>
                        <h2 className="font-display mt-4 max-w-2xl text-3xl font-medium tracking-tight text-[#0A1711] sm:text-4xl">
                            What people say about GGH
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <Link to="/reviews" data-testid="cta-share-review" className="btn-green">
                            Share Your Experience
                            <ArrowUpRight size={16} aria-hidden="true" />
                        </Link>
                    </Reveal>
                </div>

                {reviews === null ? (
                    <p className="mt-12 text-sm text-[#4A5D54]">Loading reviews…</p>
                ) : reviews.length === 0 ? (
                    <Reveal delay={0.25}>
                        <div
                            data-testid="reviews-empty"
                            className="mt-12 flex items-start gap-4 rounded-2xl border border-[#E8E6E1] bg-white p-8"
                        >
                            <MessageSquareQuote size={26} className="mt-0.5 shrink-0 text-[#C2A160]" aria-hidden="true" />
                            <p className="text-base leading-relaxed text-[#4A5D54]">
                                GGH is a growing initiative. Reviews will appear here as hotels,
                                partners and community members share their genuine experiences —
                                and yours could be the first.
                            </p>
                        </div>
                    </Reveal>
                ) : (
                    <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="reviews-grid">
                        {reviews.slice(0, 6).map((r, i) => (
                            <Reveal key={r.id} delay={0.07 * i} y={22}>
                                <figure
                                    data-testid={`review-${r.id}`}
                                    className="card-lift flex h-full flex-col rounded-2xl border border-[#E8E6E1] bg-white p-7"
                                >
                                    <StarRow rating={r.rating} />
                                    <blockquote className="mt-5 flex-1 text-sm leading-relaxed text-[#0A1711]">
                                        “{r.text}”
                                    </blockquote>
                                    <figcaption className="mt-6 border-t border-[#E8E6E1] pt-4">
                                        <span className="block text-sm font-bold text-[#0A1711]">{r.name}</span>
                                        {r.organization && (
                                            <span className="mt-0.5 block text-xs text-[#4A5D54]">{r.organization}</span>
                                        )}
                                    </figcaption>
                                </figure>
                            </Reveal>
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
};
